import React, { useEffect, useMemo, useState } from "react";
import { fmtRange } from "@/lib/enrol";
import { api, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Percent, Copy, ListTree, Table2 } from "lucide-react";
import { useConfirm } from "@/lib/confirm";
import ClassTreeSetup from "@/admin/ClassTreeSetup";
import DateListInput from "@/admin/DateListInput";

// One unified table for the whole schedule. Each row = one class session, but the row
// pulls in batch, term, and location on the fly. Users can create/edit those from
// dropdowns via a small "+ new" affordance so they never have to leave this page.

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const SINGULAR = { batches: "Batch", terms: "Term", locations: "Location" };

export default function ClassManager() {
  const { user } = useAuth();
  const readOnly = user?.role === "instructor";
  const [refs, setRefs] = useState({ batches: [], terms: [], locations: [], sessions: [], instructors: [] });
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // { session? }
  const [view, setView] = useState("tree");
  const switchView = (v) => setView(v);
  const confirm = useConfirm();

  const loadAll = async () => {
    const [b, t, l, s] = await Promise.all([
      api.get("/admin/batches"), api.get("/admin/terms"),
      api.get("/admin/locations"), api.get("/admin/sessions"),
    ]);
    // Instructor admin list — filter role=instructor. Endpoint is gated so instructors get 403 and we just keep empty.
    let instructors = [];
    try {
      const r = await api.get("/admin/admin-users");
      instructors = (r.data || []).filter(u => u.role === "instructor" && u.active !== false);
    } catch {}
    setRefs({ batches: b.data, terms: t.data, locations: l.data, sessions: s.data, instructors });
  };
  useEffect(() => { loadAll(); }, []);

  const rows = useMemo(() => {
    const bMap = Object.fromEntries(refs.batches.map(b => [b.id, b]));
    const tMap = Object.fromEntries(refs.terms.map(t => [t.id, t]));
    const lMap = Object.fromEntries(refs.locations.map(l => [l.id, l]));
    return refs.sessions.map(s => ({
      ...s,
      batch: bMap[s.batch_id],
      term: tMap[s.term_id],
      location: lMap[s.location_id],
    }));
  }, [refs]);

  const filtered = useMemo(() => rows.filter(r => {
    if (!q) return true;
    const hay = [r.batch?.name, r.term?.name, r.location?.name, r.day, r.instructor, r.start_time].join(" ").toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [rows, q]);

  const saveSession = async (item) => {
    try {
      const body = { ...item };
      ["schedule","availability_status","spots_left"].forEach(k => delete body[k]);
      const batch = refs.batches.find(x => x.id === body.batch_id);
      const loc = refs.locations.find(x => x.id === body.location_id);
      if (batch) body.batch_name = batch.name;
      if (loc) body.location_name = loc.name;
      // Auto-derive instructor label from the picked instructor admin: "Coach FirstName"
      if (body.instructor_admin_id) {
        const adm = refs.instructors.find(u => u.id === body.instructor_admin_id);
        if (adm) {
          const first = (adm.name || adm.instructor_name || adm.email || "").trim().split(/\s+/)[0] || "";
          const capitalized = first ? first[0].toUpperCase() + first.slice(1).toLowerCase() : "";
          if (capitalized) body.instructor = `Coach ${capitalized}`;
        }
      }
      if (body.id) await api.put(`/admin/sessions/${body.id}`, body);
      else await api.post(`/admin/sessions`, body);
      toast.success("Saved"); setEditing(null); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const delSession = async (id) => {
    const ok = await confirm({ title: "Delete this session?", description: "The batch, term, and location will stay — just this row is removed.", danger: true, confirmLabel: "Delete session" });
    if (!ok) return;
    try { await api.delete(`/admin/sessions/${id}`); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Failed"); }
  };

  const cloneRow = (row) => {
    const copy = { ...row };
    delete copy.id; delete copy.created_at; delete copy.updated_at;
    copy.registration_count = 0;
    setEditing(copy);
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1600px]">
      <div className="flex justify-between items-end gap-4 flex-wrap mb-6">
        <div>
          <div className="eyebrow mb-2">Class Setup</div>
          <h1 className="font-display text-5xl uppercase">Classes</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">Set up everything in one place: <strong>Term → Location → Batches</strong>. Each batch row carries its name, age range, price, day &amp; time and instructor.</p>
        </div>
        <div className="flex border border-white/10 text-xs uppercase tracking-widest">
          <button onClick={() => switchView("tree")} className={`px-3 py-2 flex items-center gap-1 ${view === "tree" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`} data-testid="view-tree"><ListTree size={12}/>Setup</button>
          <button onClick={() => switchView("table")} className={`px-3 py-2 flex items-center gap-1 ${view === "table" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`} data-testid="view-table"><Table2 size={12}/>Table</button>
        </div>
      </div>

      {view === "tree" && (
        <ClassTreeSetup refs={refs} readOnly={readOnly} onChange={loadAll} onEditSession={(s) => setEditing(s)} />
      )}

      {view === "table" && (<>
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <input placeholder="Search by batch, term, location, day…" value={q} onChange={e => setQ(e.target.value)} className="bg-black border border-white/10 px-4 py-2 text-sm w-96" data-testid="search-sessions" />
        {!readOnly && (
          <button onClick={() => setEditing({})} className="btn-red !py-2 !px-3 !text-xs" data-testid="add-session"><Plus size={14}/>Add class row</button>
        )}
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-[10px]">
              <th className="text-left px-3 py-3">Term</th>
              <th className="text-left px-3 py-3">Batch / Class</th>
              <th className="text-left px-3 py-3">Day &amp; time</th>
              <th className="text-left px-3 py-3">Location</th>
              <th className="text-left px-3 py-3">Instructor</th>
              <th className="text-left px-3 py-3">Price</th>
              <th className="text-left px-3 py-3">Classes</th>
              <th className="text-left px-3 py-3">Cap</th>
              <th className="text-left px-3 py-3">Pro-rata</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`row-${r.id}`}>
                <td className="px-3 py-3 whitespace-nowrap">
                  <div className="text-white">{r.term?.name || "—"}</div>
                  <div className="text-[10px] text-zinc-500">{r.term ? `${r.term.start_date} → ${r.term.end_date}` : "no term linked"}</div>
                </td>
                <td className="px-3 py-3">
                  <div className="text-white">{r.batch?.name || r.batch_name || "—"}</div>
                  <div className="text-[10px] text-zinc-500">{r.batch?.age_range || ""}</div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <div className="text-white">{r.day || "—"}</div>
                  <div className="text-[10px] text-zinc-500">{fmtRange(r.start_time, r.end_time)}</div>
                </td>
                <td className="px-3 py-3">{r.location?.name || r.location_name || "—"}</td>
                <td className="px-3 py-3">{r.instructor || "—"}</td>
                <td className="px-3 py-3 whitespace-nowrap">
                  ${r.price ?? r.term?.price ?? "—"}
                </td>
                <td className="px-3 py-3">{r.classes_per_term ?? r.term?.classes_count ?? "—"}</td>
                <td className="px-3 py-3">
                  <span className={`text-xs ${(r.registration_count||0) >= (r.capacity||20) ? "text-red-500" : "text-zinc-400"}`}>
                    {r.registration_count ?? 0}/{r.capacity ?? 20}
                  </span>
                </td>
                <td className="px-3 py-3">
                  {r.prorata_enabled === false
                    ? <span className="text-zinc-500 text-xs">off</span>
                    : <span className="text-emerald-400 flex items-center gap-1 text-xs"><Percent size={10}/>on</span>}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {!readOnly && (<>
                    <button onClick={() => cloneRow(r)} className="text-zinc-400 hover:text-white p-1" title="Clone row" data-testid={`clone-${r.id}`}><Copy size={14}/></button>
                    <button onClick={() => setEditing(r)} className="text-zinc-400 hover:text-white p-1 ml-1" title="Edit" data-testid={`edit-${r.id}`}><Pencil size={14}/></button>
                    <button onClick={() => delSession(r.id)} className="text-zinc-400 hover:text-red-500 p-1 ml-1" title="Delete row" data-testid={`delete-${r.id}`}><Trash2 size={14}/></button>
                  </>)}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="text-center py-16 text-zinc-500">No class rows yet. Click <strong>Add class row</strong> to start.</td></tr>}
          </tbody>
        </table>
      </div>
      </>)}

      {editing && (
        <SessionEditor
          item={editing}
          refs={refs}
          onClose={() => setEditing(null)}
          onSave={saveSession}
          onRefsChange={loadAll}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

// ---- Session editor with inline "+ new" for related entities ----
function SessionEditor({ item, refs, onClose, onSave, onRefsChange, readOnly }) {
  const [form, setForm] = useState({
    prorata_enabled: item.prorata_enabled === false ? false : true,
    active: item.active === false ? false : true,
    capacity: item.capacity ?? 20,
    ...item,
  });
  const [quickAdd, setQuickAdd] = useState(null); // {kind}
  const [preview, setPreview] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    if (item?.id) api.get(`/pricing/class-preview/${item.id}`).then(r => { if (!cancelled) setPreview(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [item?.id]);

  // When a term is picked, pre-fill price + classes_per_term from that term as a helpful default.
  useEffect(() => {
    if (!form.term_id) return;
    const term = refs.terms.find(t => t.id === form.term_id);
    if (!term) return;
    setForm(f => ({
      ...f,
      price_per_class: f.price_per_class ?? term.price_per_class,
      classes_per_term: f.classes_per_term ?? term.classes_count,
    }));
    // eslint-disable-next-line
  }, [form.term_id]);

  // When an instructor admin is picked, auto-fill the instructor label as "Coach FirstName".
  useEffect(() => {
    if (!form.instructor_admin_id) return;
    const adm = refs.instructors.find(u => u.id === form.instructor_admin_id);
    if (!adm) return;
    const first = (adm.name || adm.instructor_name || adm.email || "").trim().split(/\s+/)[0] || "";
    const cap = first ? first[0].toUpperCase() + first.slice(1).toLowerCase() : "";
    if (cap) setForm(f => ({ ...f, instructor: `Coach ${cap}` }));
    // eslint-disable-next-line
  }, [form.instructor_admin_id]);

  const Select = ({ label, value, onChange, options, kind, testId }) => (
    <div>
      <label className="eyebrow block mb-1 flex items-center justify-between">{label}
        {!readOnly && <button type="button" onClick={() => setQuickAdd({ kind })} className="text-red-500 text-[10px] hover:underline" data-testid={`quick-add-${kind}`}>+ new</button>}
      </label>
      <select value={value || ""} onChange={e => onChange(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid={testId}>
        <option value="">—</option>
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="card-surface p-8 max-w-3xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-3xl uppercase">{item.id ? "Edit" : "New"} class row</h2>
          <button onClick={onClose}><X size={20}/></button>
        </div>

        <fieldset disabled={readOnly}>
        <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-2">
          <Select label="Term" value={form.term_id} onChange={v => set("term_id", v)}
            options={refs.terms.map(t => ({ id: t.id, label: `${t.name} · ${t.start_date} → ${t.end_date}` }))}
            kind="terms" testId="select-term"/>
          <Select label="Batch / Class" value={form.batch_id} onChange={v => set("batch_id", v)}
            options={refs.batches.map(b => ({ id: b.id, label: `${b.name}${b.age_range ? " · " + b.age_range : ""}` }))}
            kind="batches" testId="select-batch"/>
          <Select label="Location" value={form.location_id} onChange={v => set("location_id", v)}
            options={refs.locations.map(l => ({ id: l.id, label: l.name }))}
            kind="locations" testId="select-location"/>
          <div>
            <label className="eyebrow block mb-1">Day</label>
            <select value={form.day || ""} onChange={e => set("day", e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="select-day">
              <option value="">—</option>
              {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow block mb-1">Start time</label>
            <input type="time" value={form.start_time || ""} onChange={e => set("start_time", e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="input-start-time"/>
          </div>
          <div>
            <label className="eyebrow block mb-1">End time</label>
            <input type="time" value={form.end_time || ""} onChange={e => set("end_time", e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="input-end-time"/>
          </div>
          <div>
            <label className="eyebrow block mb-1">Instructor</label>
            <select value={form.instructor_admin_id || ""} onChange={e => set("instructor_admin_id", e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="select-instructor-admin">
              <option value="">— Unassigned —</option>
              {refs.instructors.map(u => {
                const first = (u.name || u.instructor_name || u.email || "").trim().split(/\s+/)[0] || "";
                const cap = first ? first[0].toUpperCase() + first.slice(1).toLowerCase() : "";
                return <option key={u.id} value={u.id}>Coach {cap} · {u.email}</option>;
              })}
            </select>
            {form.instructor && <div className="text-[10px] text-zinc-500 mt-1">Displayed as: <strong className="text-zinc-300">{form.instructor}</strong></div>}
          </div>
          <div>
            <label className="eyebrow block mb-1">Price per class ($)</label>
            <input type="number" step="0.5" value={form.price_per_class ?? ""} onChange={e => set("price_per_class", e.target.value === "" ? "" : Number(e.target.value))} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="input-price-per-class"/>
            <div className="text-[10px] text-emerald-400 mt-1" data-testid="edit-total-preview">{form.price_per_class !== "" && form.price_per_class != null ? `× ${form.classes_per_term || 0} classes = $${(Number(form.price_per_class) * Number(form.classes_per_term || 0)).toFixed(2)} term price` : "Set a price per class — the term price is calculated automatically"}</div>
          </div>
          <div>
            <label className="eyebrow block mb-1">Instructor pay per class ($)</label>
            <input type="number" step="0.01" value={form.pay_amount ?? ""} onChange={e => set("pay_amount", e.target.value === "" ? "" : Number(e.target.value))} placeholder="e.g. 65.00" className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="input-pay-amount"/>
          </div>
          <div>
            <label className="eyebrow block mb-1">Classes per term</label>
            <input type="number" value={form.classes_per_term ?? ""} onChange={e => set("classes_per_term", e.target.value === "" ? "" : Number(e.target.value))} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="input-classes-per-term"/>
          </div>
          <div>
            <label className="eyebrow block mb-1">Capacity</label>
            <input type="number" value={form.capacity ?? ""} onChange={e => set("capacity", e.target.value === "" ? "" : Number(e.target.value))} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="input-capacity"/>
          </div>
          <div>
            <label className="eyebrow block mb-1">Pro-rata pricing</label>
            <select value={form.prorata_enabled === false ? "false" : "true"} onChange={e => set("prorata_enabled", e.target.value === "true")} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="select-prorata">
              <option value="true">On — charge only for remaining classes</option>
              <option value="false">Off — always charge full term price</option>
            </select>
          </div>
          <div className="col-span-2">
            <DateListInput value={form.skip_dates || []} onChange={v => set("skip_dates", v)} label="Rescheduled / no-class dates for this row" hint="Only this class row skips these dates; its last class moves a week later per skipped date." testId="row-skip-dates" />
          </div>
          <div>
            <label className="eyebrow block mb-1">Active</label>
            <select value={form.active === false ? "false" : "true"} onChange={e => set("active", e.target.value === "true")} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="select-active">
              <option value="true">Yes</option><option value="false">No</option>
            </select>
          </div>
        </div>
        </fieldset>

        {preview && (
          <div className="mt-4 border border-amber-500/30 bg-amber-500/5 p-4 text-xs" data-testid="prorata-preview">
            <div className="eyebrow mb-2 text-amber-300 flex items-center gap-2"><Percent size={12}/>Pro-rata preview (today)</div>
            {preview.prorata_applied ? (
              <div className="text-zinc-300">
                {preview.remaining_classes} of {preview.total_classes} classes remaining · ${preview.price_per_class?.toFixed(2)}/class · <strong className="text-white">${preview.adjusted_price?.toFixed(2)}</strong> new-signup price
                <span className="text-zinc-500 line-through ml-2">${preview.original_price?.toFixed(2)}</span>
              </div>
            ) : (
              <div className="text-zinc-400">{preview.reason || "Not applicable right now"} · Full price ${Number(preview.original_price ?? 0).toFixed(2)}</div>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-6">
          {!readOnly && <button onClick={() => onSave(form)} className="btn-red flex-1" data-testid="save-item">Save class row</button>}
          <button onClick={onClose} className="btn-ghost flex-1">{readOnly ? "Close" : "Cancel"}</button>
        </div>

        {quickAdd && (
          <QuickAdd kind={quickAdd.kind} onClose={() => setQuickAdd(null)} onDone={async (newDoc) => {
            await onRefsChange();
            setQuickAdd(null);
            // auto-select the newly created record in the correct field
            if (quickAdd.kind === "batches") set("batch_id", newDoc.id);
            if (quickAdd.kind === "terms") set("term_id", newDoc.id);
            if (quickAdd.kind === "locations") set("location_id", newDoc.id);
          }}/>
        )}
      </div>
    </div>
  );
}

// Quick add for terms / batches / locations
function QuickAdd({ kind, onClose, onDone }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const fields = {
    batches: [
      { k: "name", l: "Name", type: "text" },
      { k: "age_range", l: "Age range", type: "text" },
      { k: "classes_per_term", l: "Classes per term", type: "number" },
    ],
    terms: [
      { k: "name", l: "Name", type: "text" },
      { k: "start_date", l: "Start date", type: "date" },
      { k: "end_date", l: "End date", type: "date" },
      { k: "classes_count", l: "Classes count", type: "number" },
      { k: "price_per_class", l: "Price per class", type: "number" },
    ],
    locations: [
      { k: "name", l: "Name", type: "text" },
      { k: "address", l: "Address", type: "text" },
      { k: "phone", l: "Phone", type: "text" },
    ],
  }[kind];

  const submit = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      ["price","price_per_class","classes_per_term","classes_count"].forEach(k => { if (body[k] !== undefined && body[k] !== "") body[k] = Number(body[k]); });
      const { data } = await api.post(`/admin/${kind}`, body);
      toast.success(`Added ${SINGULAR[kind] || kind}`);
      onDone(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-[60] grid place-items-center p-6" onClick={onClose}>
      <div className="card-surface p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-2xl uppercase">New {SINGULAR[kind] || kind}</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {fields.map(f => (
            <div key={f.k} className={f.k === "name" || f.k === "address" ? "col-span-2" : ""}>
              <label className="eyebrow block mb-1">{f.l}</label>
              <input type={f.type} value={form[f.k] ?? ""} onChange={e => setForm({ ...form, [f.k]: e.target.value })} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid={`qa-${kind}-${f.k}`} />
            </div>
          ))}
        </div>
        <button onClick={submit} disabled={saving || !form.name} className="btn-red w-full mt-4" data-testid={`qa-${kind}-submit`}>
          {saving ? "Saving…" : `Create ${SINGULAR[kind] || kind}`}
        </button>
      </div>
    </div>
  );
}
